/* @jsx h */
import { defineComponent, h, ref, computed, watch } from "vue";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useLocalization } from "../Localization";

interface CalendarProps {
  month: Dayjs;
  showPrevNav: boolean;
  showNextNav: boolean;
  startDate: string | null;
  endDate: string | null;
  onDateClick: (date: string) => void;
  onMonthChange: (month: Dayjs) => void;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/** 2024-01-01 is a Monday; used purely as a seed for localised weekday names. */
const MONDAY_SEED = new Date(2024, 0, 1);

type PickerMode = "none" | "month" | "year";

/**
 * Deliberately avoids daisyUI's `btn` classes: this theme gives buttons a
 * border, which turned the calendar header into a row of things that looked
 * like form fields. Everything here is plain type with a hover wash instead.
 */
const GHOST_BTN =
  "flex items-center justify-center rounded-lg transition-colors duration-150 " +
  "text-base-content/45 hover:bg-base-200 hover:text-base-content";

export const Calendar = defineComponent<CalendarProps>({
  name: "Calendar",
  props: [
    "month",
    "showPrevNav",
    "showNextNav",
    "startDate",
    "endDate",
    "onDateClick",
    "onMonthChange",
  ],
  setup(props) {
    const { locale } = useLocalization();
    const today = dayjs().format("YYYY-MM-DD");
    const picker = ref<PickerMode>("none");
    const yearPageStart = ref(props.month.year() - 5);

    watch(
      () => props.month.year(),
      (year) => {
        if (year < yearPageStart.value || year > yearPageStart.value + 11) {
          yearPageStart.value = year - 5;
        }
      },
    );

    // Labels follow the app's chosen language rather than dayjs's global
    // locale, which app code mutates on import and which previously left
    // English weekday headers sitting under localised month names.
    const intlLocale = computed(() => locale.value || "en");

    const weekdays = computed(() => {
      const fmt = new Intl.DateTimeFormat(intlLocale.value, {
        weekday: "short",
      });
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(MONDAY_SEED);
        d.setDate(d.getDate() + i);
        return fmt.format(d);
      });
    });

    const monthLabels = computed(() => {
      const fmt = new Intl.DateTimeFormat(intlLocale.value, { month: "short" });
      return Array.from({ length: 12 }, (_, i) =>
        fmt.format(new Date(2024, i, 1)),
      );
    });

    const monthTitle = computed(() =>
      new Intl.DateTimeFormat(intlLocale.value, { month: "long" }).format(
        props.month.toDate(),
      ),
    );

    const range = computed(() => {
      const s = props.startDate;
      const e = props.endDate;
      if (!s) return { start: null, end: null };
      if (!e) return { start: s, end: null };
      return s <= e ? { start: s, end: e } : { start: e, end: s };
    });

    const cells = computed(() => {
      const m = props.month;
      // Monday-first, matching the isoWeek basis the range presets use.
      const offset = (m.startOf("month").day() + 6) % 7;
      const y = m.year();
      const mo = m.month();
      const out: Array<{ date: string; day: number } | null> = [];
      for (let i = 0; i < offset; i++) out.push(null);
      for (let d = 1; d <= m.daysInMonth(); d++)
        out.push({ date: `${y}-${pad2(mo + 1)}-${pad2(d)}`, day: d });
      return out;
    });

    const goMonth = (delta: number) =>
      props.onMonthChange(props.month.add(delta, "month"));

    const selectMonth = (i: number) => {
      props.onMonthChange(props.month.month(i));
      picker.value = "none";
    };

    const selectYear = (y: number) => {
      props.onMonthChange(props.month.year(y));
      picker.value = "month";
    };

    const chevron = (dir: "prev" | "next") => (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-4 w-4"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline
          points={dir === "prev" ? "15 18 9 12 15 6" : "9 18 15 12 9 6"}
        />
      </svg>
    );

    const navButton = (
      dir: "prev" | "next",
      onClick: () => void,
      visible = true,
    ) => (
      <button
        type="button"
        onClick={onClick}
        aria-label={dir === "prev" ? "Previous" : "Next"}
        class={[
          GHOST_BTN,
          "h-8 w-8 shrink-0",
          visible ? "" : "invisible pointer-events-none",
        ].join(" ")}
      >
        {chevron(dir)}
      </button>
    );

    const overlayGrid = (
      items: Array<{ key: string | number; label: string; selected: boolean }>,
      onPick: (index: number) => void,
    ) => (
      <div class="grid grid-cols-3 gap-1.5 pt-1">
        {items.map((item, i) => (
          <button
            key={item.key}
            type="button"
            onClick={() => onPick(i)}
            class={[
              "h-10 rounded-lg text-[13px] font-medium capitalize",
              "transition-colors duration-150",
              item.selected
                ? "bg-primary text-primary-content"
                : "text-base-content/75 hover:bg-base-200 hover:text-base-content",
            ].join(" ")}
          >
            {item.label}
          </button>
        ))}
      </div>
    );

    return () => {
      const { start, end } = range.value;

      /* ── Month overlay ─────────────────────────────────────────────── */
      if (picker.value === "month") {
        const cur = props.month.month();
        return (
          <div class="w-full sm:w-[268px]">
            <div class="flex h-9 items-center justify-between">
              <button
                type="button"
                class="rounded-md px-2 py-1 text-sm font-semibold text-primary transition-colors hover:bg-base-200"
                onClick={() => (picker.value = "year")}
              >
                {props.month.year()}
              </button>
              <button
                type="button"
                aria-label="Close month picker"
                class={[GHOST_BTN, "h-8 w-8"].join(" ")}
                onClick={() => (picker.value = "none")}
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            {overlayGrid(
              monthLabels.value.map((label, i) => ({
                key: i,
                label,
                selected: cur === i,
              })),
              selectMonth,
            )}
          </div>
        );
      }

      /* ── Year overlay ──────────────────────────────────────────────── */
      if (picker.value === "year") {
        const curY = props.month.year();
        const years = Array.from(
          { length: 12 },
          (_, i) => yearPageStart.value + i,
        );
        return (
          <div class="w-full sm:w-[268px]">
            <div class="flex h-9 items-center justify-between">
              {navButton("prev", () => (yearPageStart.value -= 12))}
              <span class="text-sm font-semibold text-base-content/70">
                {yearPageStart.value} – {yearPageStart.value + 11}
              </span>
              {navButton("next", () => (yearPageStart.value += 12))}
            </div>
            {overlayGrid(
              years.map((year) => ({
                key: year,
                label: String(year),
                selected: curY === year,
              })),
              (i) => selectYear(years[i]),
            )}
          </div>
        );
      }

      /* ── Day calendar ───────────────────────────────────────────────── */
      return (
        <div class="w-full sm:w-[268px]">
          <div class="flex h-9 items-center justify-between">
            {navButton("prev", () => goMonth(-1), props.showPrevNav)}
            <div class="flex items-center gap-0.5">
              <button
                type="button"
                class={[
                  "rounded-md px-1.5 py-1 text-sm font-semibold capitalize",
                  "text-base-content transition-colors duration-150",
                  "hover:bg-base-200 hover:text-primary",
                ].join(" ")}
                onClick={() => (picker.value = "month")}
              >
                {monthTitle.value}
              </button>
              <button
                type="button"
                class={[
                  "rounded-md px-1.5 py-1 text-sm font-semibold tabular-nums",
                  "text-base-content/45 transition-colors duration-150",
                  "hover:bg-base-200 hover:text-primary",
                ].join(" ")}
                onClick={() => (picker.value = "year")}
              >
                {props.month.year()}
              </button>
            </div>
            {navButton("next", () => goMonth(1), props.showNextNav)}
          </div>

          <div class="grid grid-cols-7 pb-0.5 pt-1.5">
            {weekdays.value.map((wd) => (
              <div
                key={wd}
                class="flex h-6 items-center justify-center text-[10px] font-semibold uppercase text-base-content/30"
              >
                {wd}
              </div>
            ))}
          </div>

          <div class="grid grid-cols-7">
            {cells.value.map((cell, i) => {
              if (!cell) return <div key={`e-${i}`} class="h-11 sm:h-10" />;

              const { date, day } = cell;
              const isStart = date === start;
              const isEnd = date === end;
              const inRange = !!(start && end && date > start && date < end);
              const isSingle = isStart && isEnd;
              const isToday = date === today;
              const hasEnd = end !== null;
              const showBand =
                !isSingle && (inRange || (isStart && hasEnd) || isEnd);

              return (
                <button
                  key={date}
                  type="button"
                  class="group relative flex h-11 items-center justify-center sm:h-10"
                  onClick={() => props.onDateClick(date)}
                >
                  {showBand && (
                    <div
                      class={[
                        "pointer-events-none absolute inset-y-0.5 bg-primary/10",
                        isStart ? "left-1/2 rounded-l-full" : "left-0",
                        isEnd ? "right-1/2 rounded-r-full" : "right-0",
                      ].join(" ")}
                    />
                  )}

                  <div
                    class={[
                      "pointer-events-none relative z-10 flex h-9 w-9 select-none items-center justify-center",
                      "rounded-full text-[13px] tabular-nums transition-colors duration-100",
                      isStart || isEnd
                        ? "bg-primary font-semibold text-primary-content"
                        : isToday
                          ? "font-semibold text-primary ring-1 ring-inset ring-primary/40 group-hover:bg-base-200"
                          : inRange
                            ? "font-medium text-base-content group-hover:bg-primary/20"
                            : "font-medium text-base-content/70 group-hover:bg-base-200 group-hover:text-base-content",
                    ].join(" ")}
                  >
                    {day}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      );
    };
  },
});
